import { useContext } from "react";
import {
  parseDate,
  today,
  getLocalTimeZone,
  type DateValue,
} from "@internationalized/date";
import {
  Button,
  Calendar,
  CalendarCell,
  CalendarGrid,
  CalendarStateContext,
  DateInput,
  DatePicker,
  DateSegment,
  Dialog,
  FieldError,
  Group,
  I18nProvider,
  Label,
  Popover,
} from "react-aria-components";
import { useTranslation } from "react-i18next";
import { Icon } from "@core/assets";
import { cn } from "@core/utils";

interface DateFieldProps {
  /** Accessible label. Always required; hide it visually with `hideLabel`. */
  label: string;
  hideLabel?: boolean;
  /** ISO calendar date (`2027-01-03`), or an empty string while no date is chosen. */
  value: string;
  /** Called with the ISO date, or `""` when the date is cleared or incomplete. */
  onChange: (isoDate: string) => void;
  onBlur?: () => void;
  isDisabled?: boolean;
  isRequired?: boolean;
  errorMessage?: string;
}

/** Day first in both languages: « 03/01/2027 ». */
const LOCALES = { fr: "fr-FR", en: "en-GB" } as const;

const NAV_BUTTON =
  "flex size-8 items-center justify-center rounded-full text-(--text-secondary) outline-none " +
  "hover:bg-ink-100 pressed:bg-ink-100 focus-visible:ring-2 focus-visible:ring-focus-ring";

const SELECT =
  "rounded-md border border-(--border-default) bg-surface-field px-xs py-1 text-sm-semibold " +
  "text-(--text-primary) outline-none focus-visible:ring-2 focus-visible:ring-focus-ring";

/** Years offered in the calendar: a few behind today, many ahead (programmes are planned ahead). */
const YEARS_BEHIND = 5;
const YEARS_AHEAD = 15;

/**
 * The calendar's title: instead of a fixed « janvier 2027 », a month list and a year list, so a
 * date far from today is reached in two choices instead of many arrows. Reads and moves the
 * calendar's own focused date, so the grid, the arrows and the keyboard stay in step.
 */
function MonthYearPicker({ locale }: { locale: string }) {
  const { t } = useTranslation("common");
  const state = useContext(CalendarStateContext);
  if (!state) return null;

  const focused = state.focusedDate;
  const monthNames = Array.from({ length: 12 }, (_, index) =>
    new Intl.DateTimeFormat(locale, { month: "long" }).format(
      new Date(2000, index, 1)
    )
  );
  const thisYear = today(getLocalTimeZone()).year;
  const first = Math.min(thisYear - YEARS_BEHIND, focused.year);
  const last = Math.max(thisYear + YEARS_AHEAD, focused.year);
  const years = Array.from({ length: last - first + 1 }, (_, i) => first + i);

  return (
    <div className="flex items-center gap-xs">
      <select
        aria-label={t("date-field.month")}
        className={cn(SELECT, "capitalize")}
        value={focused.month}
        onChange={(event) => {
          state.setFocusedDate(
            focused.set({ month: Number(event.target.value) })
          );
        }}
      >
        {monthNames.map((name, index) => (
          <option key={name} value={index + 1}>
            {name}
          </option>
        ))}
      </select>
      <select
        aria-label={t("date-field.year")}
        className={SELECT}
        value={focused.year}
        onChange={(event) => {
          state.setFocusedDate(
            focused.set({ year: Number(event.target.value) })
          );
        }}
      >
        {years.map((year) => (
          <option key={year} value={year}>
            {year}
          </option>
        ))}
      </select>
    </div>
  );
}

function toDateValue(iso: string): DateValue | null {
  try {
    return iso ? parseDate(iso) : null;
  } catch {
    return null;
  }
}

/**
 * Date picker, as drawn for the programme start and end: the box shows the day-first date
 * (« 03/01/2027 »), typed by segment or chosen from a calendar opened by the trailing icon. The
 * value that goes in and out stays an ISO date, so callers and the API see no difference.
 *
 * Built on React Aria's `DatePicker`: keyboard entry, the calendar's arrow-key navigation, focus
 * return and the labelling come from the library. An incomplete date reads as `""`, so a required
 * rule fails on it.
 */
export function DateField({
  label,
  hideLabel = false,
  value,
  onChange,
  onBlur,
  isDisabled = false,
  isRequired,
  errorMessage,
}: DateFieldProps) {
  const { t, i18n } = useTranslation("common");
  const invalid = Boolean(errorMessage);
  const locale = i18n.language.startsWith("en") ? LOCALES.en : LOCALES.fr;

  return (
    <I18nProvider locale={locale}>
      <DatePicker
        value={toDateValue(value)}
        onChange={(date) => {
          onChange(date ? date.toString() : "");
        }}
        onBlur={onBlur}
        isDisabled={isDisabled}
        isRequired={isRequired}
        isInvalid={invalid}
        validationBehavior="aria"
        className="flex w-full flex-col gap-1.5"
      >
        <Label
          className={cn(
            "text-sm-medium text-(--text-secondary)",
            hideLabel && "sr-only"
          )}
        >
          {label}
        </Label>

        <Group
          className={cn(
            "flex items-center gap-xs rounded-md border py-2.5 pr-2.5 pl-3.5 transition-colors",
            isDisabled
              ? "bg-surface-field-disabled"
              : "bg-surface-field shadow-xs",
            invalid
              ? "border-(--border-field-error) focus-within:ring-1 focus-within:ring-(--border-field-error)"
              : "border-(--border-field) focus-within:border-(--border-field-focus) focus-within:ring-1 focus-within:ring-(--border-field-focus)"
          )}
        >
          <DateInput className="flex min-w-0 flex-1 items-center text-md-regular text-(--text-primary)">
            {(segment) => (
              <DateSegment
                segment={segment}
                className={cn(
                  "rounded-xs px-px tabular-nums outline-none",
                  "focus:bg-ink-100 data-[placeholder]:text-(--text-placeholder)",
                  "data-[disabled]:text-(--text-disabled)"
                )}
              />
            )}
          </DateInput>
          <Button
            aria-label={t("date-field.open")}
            className={cn(NAV_BUTTON, "size-7 shrink-0")}
          >
            <Icon name="calendar" size={18} />
          </Button>
        </Group>

        <FieldError className="text-sm-regular text-(--text-error)">
          {errorMessage}
        </FieldError>

        <Popover
          placement="bottom end"
          className="rounded-lg border border-(--border-default) bg-surface-card p-md shadow-menu outline-none entering:animate-in entering:fade-in entering:zoom-in-95 exiting:animate-out exiting:fade-out exiting:zoom-out-95"
        >
          <Dialog className="outline-none">
            <Calendar className="flex w-64 flex-col gap-sm">
              <header className="flex items-center justify-between">
                <Button slot="previous" className={NAV_BUTTON}>
                  <Icon name="arrow-left" size={16} />
                </Button>
                <MonthYearPicker locale={locale} />
                <Button slot="next" className={NAV_BUTTON}>
                  <Icon name="arrow-right" size={16} />
                </Button>
              </header>
              <CalendarGrid className="border-separate border-spacing-xxs">
                {(date) => (
                  <CalendarCell
                    date={date}
                    className={cn(
                      "flex size-8 cursor-pointer items-center justify-center rounded-full text-sm-regular outline-none",
                      "text-(--text-primary) hover:bg-ink-100",
                      "data-[outside-month]:text-(--text-disabled)",
                      "data-[today]:font-semibold",
                      "data-[selected]:bg-ink-900 data-[selected]:text-white",
                      "data-[focus-visible]:ring-2 data-[focus-visible]:ring-focus-ring"
                    )}
                  />
                )}
              </CalendarGrid>
            </Calendar>
          </Dialog>
        </Popover>
      </DatePicker>
    </I18nProvider>
  );
}

export type { DateFieldProps };
